import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Accordion, Box, Button, Group, rem, Stack } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { useLaborEncounterCloseoutGuard } from "@/clients/hooks/useLaborEncounterCloseoutGuard";
import ConfirmationModal from "@/components/ConfirmationModal";
import GAAlertMessageCard from "@/components/GAAlertMessage/GAAlertMessageCard";
import { TextWrapper } from "@/components/TextWrapper";
import { calculateGestationalAgeInWeeksAndDays, isGestationalAgeOverdue } from "@/pregnancy";
import { selectCurrentPregnancyRecord } from "@/store/slices/pregnancy";
import { usDateFormat } from "@/utils/datetime";

import CloseoutDrawer from "./CloseoutDrawer";
import CloseoutWarning from "./CloseoutWarning";

export default function CloseoutAccordion({
  trigger,
  closeGTPALDrawer,
}: {
  trigger: () => void;
  closeGTPALDrawer: () => void;
}) {
  const { clientId } = useParams() as { clientId: string };

  // New primary path: derive values from the active PregnancyRecord.
  const activePregnancyRecord = useSelector(selectCurrentPregnancyRecord(clientId));
  const eddFromRecord = activePregnancyRecord?.edd;
  const gestationFromRecord = useMemo(
    () => (eddFromRecord ? calculateGestationalAgeInWeeksAndDays(eddFromRecord) : undefined),
    [eddFromRecord],
  );

  const isPregnant = activePregnancyRecord?.isActive ?? false;
  const formattedEdd = eddFromRecord ? usDateFormat(eddFromRecord) : "--";
  const gestation = gestationFromRecord;

  const [opened, { open, close }] = useDisclosure(false);

  const [closeoutDrawerOpened, { open: openCloseoutDrawer, close: closeCloseoutDrawer }] =
    useDisclosure(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const {
    closeConfirmation,
    confirmLaborEncounterNavigation,
    confirmationModalProps,
    isConfirmationOpened,
    requestCloseout,
  } = useLaborEncounterCloseoutGuard({ clientId, prefetchEnabled: isPregnant });

  const gestationDisplay =
    gestation && (gestation.weeks || gestation.days)
      ? `GA ${gestation.weeks ?? 0}w ${gestation.days ?? 0}d`
      : "GA --";
  const eddDisplay = `EDD ${formattedEdd}`;

  const onCloseOut = async () => {
    const canProceed = await requestCloseout();
    if (canProceed) {
      openCloseoutDrawer();
    }
  };

  const onConfirmNavigation = () => {
    confirmLaborEncounterNavigation(() => {
      closeCloseoutDrawer();
      closeGTPALDrawer();
    });
  };

  const isGAOver40weeks = isGestationalAgeOverdue(isPregnant, gestation);

  useEffect(() => {
    if (isGAOver40weeks && isPregnant) {
      setIsAlertVisible(true);
    }
  }, [isGAOver40weeks, isPregnant]);

  const handleCloseAlert = () => {
    setIsAlertVisible(false);
  };

  if (!isPregnant) return <CloseoutWarning />;

  return (
    <>
      {isGAOver40weeks && isAlertVisible && <GAAlertMessageCard onClose={handleCloseAlert} />}
      <Accordion
        radius={"var(--mantine-radius-md)"}
        value={opened ? "current-pregnancy" : null}
        onChange={(val) => (val === "current-pregnancy" ? open() : close())}
      >
        <Accordion.Item
          value="current-pregnancy"
          style={{
            borderRadius: "var(--mantine-radius-md)",
            border: "1px solid var(--mantine-color-gray-3)",
            boxShadow: "0 1px 3px rgba(16, 24, 40, 0.08)",
            backgroundColor: "var(--mantine-color-white)",
          }}
        >
          <Accordion.Control
            bg="var(--mantine-color-white)"
            style={{
              borderRadius: "var(--mantine-radius-md)",
            }}
          >
            <Stack gap={rem(4)}>
              <TextWrapper fz="sm" c="gray.9" variant="bold">
                Current pregnancy
              </TextWrapper>
              <Group gap="xs" wrap="nowrap" align="center">
                <Box style={{ display: "flex", gap: rem(16) }}>
                  <TextWrapper size="sm" c="gray.6">
                    {gestationDisplay}
                  </TextWrapper>
                  <TextWrapper size="sm" c="gray.6">
                    {eddDisplay}
                  </TextWrapper>
                </Box>
              </Group>
            </Stack>
          </Accordion.Control>
          {opened && (
            <Accordion.Panel px={rem(20)} py={rem(16)}>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: rem(16),
                  backgroundColor: "var(--mantine-color-gray-0)",
                  borderRadius: "var(--mantine-radius-md)",
                }}
              >
                <TextWrapper size="sm" c="gray.9" variant="bold">
                  Close out current pregnancy?
                </TextWrapper>
                <Button
                  size="sm"
                  radius="md"
                  color="red.7"
                  onClick={() => {
                    void onCloseOut();
                  }}
                >
                  Close
                </Button>
              </Box>
            </Accordion.Panel>
          )}
        </Accordion.Item>
      </Accordion>
      <ConfirmationModal
        isDeleting={false}
        {...confirmationModalProps}
        onClose={closeConfirmation}
        opened={isConfirmationOpened}
        onConfirm={onConfirmNavigation}
      />
      <CloseoutDrawer
        trigger={trigger}
        opened={closeoutDrawerOpened}
        onClose={closeCloseoutDrawer}
      />
    </>
  );
}
